import ExcelJS from "exceljs";
import { FIRST_BLOCK_ROW, MAX_EXPORT_ROWS } from "./constants";
import { buildWeekBlocks, countExportRows } from "./model/buildWeekBlocks";
import { renderWeekScheduleSheet } from "./render/renderWeekScheduleSheet";
import type { CreateWeekStaffScheduleExportArgs } from "./types";

/** Thrown when the requested range is too large to render; the caller surfaces it to the user. */
export class WeekScheduleExportTooLargeError extends Error {
	constructor(readonly rowCount: number) {
		super(`Week schedule export would produce ${rowCount} rows, above the ${MAX_EXPORT_ROWS} row limit`);
		this.name = "WeekScheduleExportTooLargeError";
	}
}

/**
 * Build the week-schedule workbook for one employee and return its bytes.
 *
 * The same buffer feeds the in-app preview and the download, so what the user approves in the popup
 * is byte-for-byte what they get.
 */
export const createWeekStaffScheduleExport = async (args: CreateWeekStaffScheduleExportArgs): Promise<ArrayBuffer> => {
	const { weeks, rowContext, dayStart, content, details, labels, colors, pageSetup, columnWidthOverrides, analytics, nurseId, departmentId } = args;
	const eventProps = { nurse_id: nurseId, department_id: departmentId, week_count: weeks.length };

	analytics?.track("week_staff_schedule_export_build_start", eventProps);
	try {
		const blocks = buildWeekBlocks({ weeks, rowContext, content, details, labels, dayStart });

		// The header occupies the rows before the first block, so it is part of the total.
		const rowCount = countExportRows(blocks, FIRST_BLOCK_ROW - 1);
		if (rowCount > MAX_EXPORT_ROWS) throw new WeekScheduleExportTooLargeError(rowCount);

		const workbook = new ExcelJS.Workbook();
		renderWeekScheduleSheet({ workbook, blocks, labels, colors, pageSetup, columnWidthOverrides });

		// exceljs types this as its own Buffer alias; every consumer here (the preview parser and the
		// browser download helper) takes an ArrayBuffer, which is what it actually returns.
		const buffer = (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
		analytics?.track("week_staff_schedule_export_build_success", { ...eventProps, row_count: rowCount });
		return buffer;
	} catch (error) {
		analytics?.trackError("week_staff_schedule_export_build_error", {
			...eventProps,
			error_message: error instanceof Error ? error.message : String(error),
		});
		throw error;
	}
};
